import React from 'react';
import PropTypes from 'prop-types';
import {
  MessageSquare,
  Bot,
  Check,
  XCircle,
  Clock,
  User,
  Bell,
} from 'lucide-react';
import { EVENT_TYPES } from '../../hooks/useWebhookEvents';

// Channel colors
const CHANNEL_COLORS = {
  zalo: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
  telegram: { bg: 'bg-sky-500/20', text: 'text-sky-400', border: 'border-sky-500/30' },
  facebook: { bg: 'bg-indigo-500/20', text: 'text-indigo-400', border: 'border-indigo-500/30' },
  whatsapp: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
};

// Event type configurations
const EVENT_CONFIG = {
  [EVENT_TYPES.NEW_MESSAGE]: {
    icon: MessageSquare,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    label: 'New message',
  },
  [EVENT_TYPES.AGENT_RESPONDING]: {
    icon: Bot,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    label: 'Agent responding',
  },
  [EVENT_TYPES.AGENT_RESPONDED]: {
    icon: Check,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    label: 'Response sent',
  },
  [EVENT_TYPES.CONVERSATION_CLOSED]: {
    icon: XCircle,
    color: 'text-slate-400',
    bgColor: 'bg-slate-500/10',
    label: 'Closed',
  },
  [EVENT_TYPES.CUSTOMER_WAITING]: {
    icon: Clock,
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    label: 'Waiting',
  },
};

// Format relative time
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffMs / 60000);

  if (diffSecs < 5) return 'just now';
  if (diffSecs < 60) return `${diffSecs}s ago`;
  if (diffMins < 60) return `${diffMins}m ago`;
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/**
 * Single event item in the feed
 */
const EventItem = ({ event, isNew }) => {
  const config = EVENT_CONFIG[event.type] || EVENT_CONFIG[EVENT_TYPES.NEW_MESSAGE];
  const channelColor = CHANNEL_COLORS[event.customer?.channel] || CHANNEL_COLORS.zalo;
  const Icon = config.icon;

  return (
    <div
      className={`
        flex items-start gap-3 p-3 rounded-xl
        ${config.bgColor}
        border border-white/5
        ${isNew ? 'animate-slide-in-right' : ''}
        transition-all duration-300
        hover:bg-white/5
      `}
    >
      {/* Event type icon */}
      <div className={`p-2 rounded-lg ${config.bgColor}`}>
        <Icon className={`w-4 h-4 ${config.color}`} />
      </div>

      {/* Event content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {/* Customer name */}
          <span className="text-sm font-medium text-white truncate">
            {event.customer?.name || 'Unknown'}
          </span>

          {/* Channel badge */}
          <span className={`
            px-1.5 py-0.5 rounded text-[10px] font-medium uppercase
            ${channelColor.bg} ${channelColor.text}
          `}>
            {event.customer?.channel || 'unknown'}
          </span>
        </div>

        {/* Message preview */}
        {event.data?.message && (
          <p className="text-xs text-white/50 truncate mb-1">
            "{event.data.message}"
          </p>
        )}

        {/* Agent assignment */}
        {event.agent && (
          <div className="flex items-center gap-1 text-xs text-white/40">
            <Bot className="w-3 h-3" />
            <span>{event.agent.name}</span>
            {event.type === EVENT_TYPES.AGENT_RESPONDED && event.data?.responseTime && (
              <span className="text-emerald-400 ml-1">
                • {event.data.responseTime}s
              </span>
            )}
          </div>
        )}
      </div>

      {/* Timestamp */}
      <span className="text-[10px] text-white/30 whitespace-nowrap">
        {formatRelativeTime(event.timestamp)}
      </span>
    </div>
  );
};

/**
 * ActivityFeed - Real-time feed of webhook events
 */
const ActivityFeed = ({
  events = [],
  maxHeight = '400px',
  showHeader = true,
  onClear,
  compact = false,
}) => {
  const hasEvents = events.length > 0;

  return (
    <div className="bg-[#0d1424] rounded-2xl border border-white/10 overflow-hidden">
      {/* Header */}
      {showHeader && (
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-[#ff4d4d]" />
            <h3 className="text-sm font-semibold text-white">Activity Feed</h3>
            {hasEvents && (
              <span className="px-2 py-0.5 bg-[#ff4d4d]/20 text-[#ff4d4d] text-xs rounded-full">
                {events.length}
              </span>
            )}
          </div>
          {onClear && hasEvents && (
            <button
              onClick={onClear}
              className="text-xs text-white/40 hover:text-white transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Events list */}
      <div
        className="overflow-y-auto p-3 space-y-2"
        style={{ maxHeight }}
      >
        {hasEvents ? (
          events.map((event, index) => (
            <EventItem
              key={event.id}
              event={event}
              isNew={index === 0}
            />
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-white/30">
            <Bell className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-sm">No activity yet</p>
            <p className="text-xs mt-1">Events will appear here when customers message</p>
          </div>
        )}
      </div>
    </div>
  );
};

ActivityFeed.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      timestamp: PropTypes.string,
      customer: PropTypes.shape({
        name: PropTypes.string,
        channel: PropTypes.string,
      }),
      agent: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
      data: PropTypes.object,
    })
  ),
  maxHeight: PropTypes.string,
  showHeader: PropTypes.bool,
  onClear: PropTypes.func,
  compact: PropTypes.bool,
};

// Add the CSS animation
const style = document.createElement('style');
style.textContent = `
  @keyframes slide-in-right {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  .animate-slide-in-right {
    animation: slide-in-right 0.3s ease-out forwards;
  }
`;
if (typeof document !== 'undefined' && !document.getElementById('activity-feed-styles')) {
  style.id = 'activity-feed-styles';
  document.head.appendChild(style);
}

export default ActivityFeed;
